<script setup>
import { addThousandsSeparator } from '~/utils/utils.js';
import Card from '~/components/Card.vue';
import InfoTooltip from '~/components/InfoTooltip.vue';
import LoadingSkeleton from '~/components/LoadingSkeleton.vue';
import CardLabel from '~/components/CardLabel.vue';

// Props
const props = defineProps({
    hashrate: { type: Number, default: 0 },
    dailyMiningRewardKas: { type: Number, default: 0 },
    dailyMiningRewardUsd: { type: Number, default: 0 },
    loading: { type: Boolean, default: false }
});
</script>

<template>
    <div class="grid grid-cols-1 md:grid-cols-3 cards-gap">
        <!-- Network Hashrate Card -->
        <Card>
            <div class="flex items-center space-x-4">
                <div class="p-3 bg-blue-50 dark:bg-blue-900/50 rounded-xl">
                    <svg class="w-6 h-6 text-blue-500 dark:text-blue-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M9 3v2m6-2v2M9 19v2m6-2v2M5 9H3m2 6H3m18-6h-2m2 6h-2M7 19h10a2 2 0 002-2V7a2 2 0 00-2-2H7a2 2 0 00-2 2v10a2 2 0 002 2zM9 9h6v6H9V9z" />
                    </svg>
                </div>
                <div>
                    <div class="flex items-center">
                        <CardLabel text="Network Hashrate" />
                        <InfoTooltip text="Total computational power of the network in TeraHashes per second" />
                    </div>
                    <div class="mt-1">
                        <LoadingSkeleton v-if="props.loading" height="28px" width="140px" />
                        <p v-else class="text-xl font-bold text-gray-900 dark:text-white slide-in-up">
                            {{ addThousandsSeparator(props.hashrate, 2) }} TH/s
                        </p>
                    </div>
                </div>
            </div>
        </Card>

        <!-- Daily Mining Rewards Card -->
        <Card>
            <div class="flex items-center space-x-4">
                <div class="p-3 bg-green-50 dark:bg-green-900/50 rounded-xl">
                    <svg class="w-6 h-6 text-green-500 dark:text-green-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <circle cx="12" cy="12" r="9" stroke-width="2"/>
                        <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M9 12h6M12 9v6"/>
                    </svg>
                </div>
                <div>
                    <div class="flex items-center">
                        <CardLabel text="Daily Mining Reward" />
                        <InfoTooltip text="Total KAS tokens distributed to miners every day, considering 1 block per second" />
                    </div>
                    <div class="mt-1">
                        <LoadingSkeleton v-if="props.loading" height="28px" width="140px" />
                        <p v-else class="text-xl font-bold text-gray-900 dark:text-white slide-in-up">
                            {{ addThousandsSeparator(props.dailyMiningRewardKas, 2) }} KAS
                        </p>
                    </div>
                </div>
            </div>
        </Card>

        <!-- Daily Mining Revenue Card -->
        <Card>
            <div class="flex items-center space-x-4">
                <div class="p-3 bg-purple-50 dark:bg-purple-900/50 rounded-xl">
                    <svg class="w-6 h-6 text-purple-500 dark:text-purple-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M12 8c-1.657 0-3 .895-3 2s1.343 2 3 2 3 .895 3 2-1.343 2-3 2m0-8c1.11 0 2.08.402 2.599 1M12 8V7m0 1v8m0 0v1m0-1c-1.11 0-2.08-.402-2.599-1M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
                    </svg>
                </div>
                <div>
                    <div class="flex items-center">
                        <CardLabel text="Daily Mining Revenue" />
                        <InfoTooltip text="Total value of daily mining rewards in USD, considering 1 block per second" />
                    </div>
                    <div class="mt-1">
                        <LoadingSkeleton v-if="props.loading" height="28px" width="140px" />
                        <p v-else class="text-xl font-bold text-gray-900 dark:text-white slide-in-up">
                            ${{ addThousandsSeparator(props.dailyMiningRewardUsd, 2) }}
                        </p>
                    </div>
                </div>
            </div>
        </Card>
    </div>
</template>